import { useQuery } from '@apollo/client';
import { Box, CircularProgress, Typography } from '@mui/material';
import { QuickViewWidget, useScrollIntoView } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { CaclRiskScore } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { SUPPLIER_RISK_SCORE_WIDGET_QUERY } from './SupplierRiskScoreWidget.gql';
import { riskScoreToLevel, riskScoreToValue } from './SupplierRiskScoreWidget.util.ts';

const WIDGET_NAME = 'SupplierRiskScoreWidget';

export const SupplierRiskScoreWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.riskSustainability, { keyPrefix: WIDGET_NAME });
  const [widgetRef, active] = useScrollIntoView({ hash: 'supplier-risk-score' });
  const { data, loading } = useQuery(SUPPLIER_RISK_SCORE_WIDGET_QUERY, { variables: { id: entityId } });
  const score = data?.cataclysm?.supplier?.score ?? CaclRiskScore.UNKNOWN;
  const value = riskScoreToValue[score];
  const color = riskScoreToLevel[score];

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      title={t('title')}
      subTitle={t('subTitle')}
      loading={loading}
      value={
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress color={color} variant="determinate" value={100} />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: 'text.primary',
              }}
            >
              {value}%
            </Typography>
          </Box>
        </Box>
      }
      valueTooltip={t('supplierNonCompliance')}
      data-intercom-target={WIDGET_NAME}
    />
  );
};
